import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  AgentDetail, 
  ShareAgentModal 
} from '@/components/showcase';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Share2 } from 'lucide-react';
import { showcaseService } from '@/services/showcase/showcaseService';
import { interactionService } from '@/services/showcase/interactionService';
import { ShowcaseItem, ShowcaseComment } from '@/types/showcase';
import { SecondMeConfig } from '@/components/second-me/types';
import { useAuth } from '@/contexts/auth/AuthContext';
import { useTitle } from '../../hooks/useTitle';

/**
 * Page component for displaying a specific showcase item
 */
const ShowcaseItemPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [item, setItem] = useState<ShowcaseItem | null>(null);
  const [config, setConfig] = useState<SecondMeConfig | null>(null);
  const [isLiked, setIsLiked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comments, setComments] = useState<ShowcaseComment[]>([]);
  const [similarItems, setSimilarItems] = useState<ShowcaseItem[]>([]);

  useTitle(item?.title || 'Agent Details');

  // Fetch the showcase item and related data
  useEffect(() => {
    const fetchShowcaseItem = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        
        // Fetch the item
        const itemData = await showcaseService.getShowcaseItem(id);
        if (!itemData) {
          setError('Showcase item not found');
          setLoading(false);
          return;
        }
        
        setItem(itemData);
        
        // Record the view
        if (user) {
          await interactionService.recordInteraction({
            userId: user.id,
            itemId: id,
            interactionType: 'view',
          });
        }
        
        await showcaseService.incrementViewCount(id);
        
        // Check if the user has liked the item
        if (user) {
          const hasLiked = await interactionService.hasUserInteracted(user.id, id, 'like');
          setIsLiked(hasLiked);
        }
        
        // Fetch the associated configuration
        // This would need to be implemented based on your data structure
        // For now, we'll simulate this
        const configData = await fetchConfiguration(itemData.contentId);
        setConfig(configData);
        
        // Fetch comments
        const commentsData = await interactionService.getItemComments(id);
        setComments(commentsData);
        
        // Fetch similar items
        const similarItemsData = await showcaseService.getSimilarItems(id);
        setSimilarItems(similarItemsData);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching showcase item:', err);
        setError('Failed to load this showcase item. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchShowcaseItem();
  }, [id, user]);

  // Mock function to fetch configuration - in a real implementation,
  // this would fetch from your actual SecondMeConfig storage
  const fetchConfiguration = async (contentId: string): Promise<SecondMeConfig> => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Return mock configuration
    return {
      id: contentId,
      name: item?.title || 'Agent Configuration',
      description: item?.description || '',
      version: '1.0.0',
      coreValues: [
        {
          name: 'Helpfulness',
          description: 'Always strives to be of assistance',
          example: 'Let me find a solution to your problem'
        },
        {
          name: 'Accuracy',
          description: 'Prioritizes factual correctness',
          example: 'Let me verify that information before proceeding'
        }
      ],
      personalityTraits: {
        openness: 85,
        conscientiousness: 90,
        extraversion: 60,
        agreeableness: 80,
        neuroticism: 25,
        explanations: {
          openness: 'Open to new ideas and experiences',
          conscientiousness: 'Organized and detail-oriented',
          extraversion: 'Moderately social and engaging',
          agreeableness: 'Cooperative and considerate',
          neuroticism: 'Emotionally stable and calm'
        }
      },
      learningStyle: {
        visualVerbal: 60,
        sequentialGlobal: 40,
        activeReflective: 30,
        sensingIntuitive: 70,
        description: 'Prefers visual information with a balanced approach to sequential and global understanding. More reflective than active, and more intuitive than sensing.'
      },
      expertiseAreas: item?.metadata?.capabilities ?
        item.metadata.capabilities.map((capability: string) => {
          // Map showcase complexity to ExpertiseArea level
          let level: 'Novice' | 'Intermediate' | 'Advanced' | 'Expert';
          
          switch (item.metadata?.complexity) {
            case 'beginner':
              level = 'Novice';
              break;
            case 'advanced':
              level = 'Expert';
              break;
            default:
              level = 'Intermediate';
          }
          return {
            name: capability,
            level,
          concepts: ['AI', 'Machine Learning', 'Natural Language Processing']
          };
        }) : [],
      l0Memory: {
        maxInteractions: 10,
        priorityTypes: ['user input', 'system instructions'],
        decayRate: 'medium',
        transferConditions: ['relevance score > 0.7'],
        processingInstructions: 'Prioritize recent interactions with high importance'
      },
      l1Memory: {
        contexts: ['conversation', 'user preferences'],
        relationshipTracking: 'moderate',
        priorityWeighting: 'recency and relevance',
        transferConditions: ['consistent patterns', 'explicit user statements'],
        processingInstructions: 'Maintain contextual awareness across sessions'
      },
      l2Memory: {
        knowledgeCategories: ['domain expertise', 'procedural knowledge'],
        retrievalMechanisms: 'semantic and keyword-based',
        updateProtocols: 'verified information only',
        integrationStrategies: 'gradual incorporation with verification',
        processingInstructions: 'Update knowledge base when confidence exceeds threshold'
      },
      roles: [
        {
          name: 'Assistant',
          purpose: 'Provide general assistance and information',
          behaviors: ['listens attentively', 'responds thoughtfully'],
          triggers: ['direct questions', 'requests for help']
        }
      ],
      interactionPatterns: {
        greeting: 'Warm and professional',
        clarification: 'Asks specific questions to understand needs',
        explanation: 'Clear and concise with examples when needed',
        disagreement: 'Respectful and evidence-based',
        conclusion: 'Summarizes and confirms understanding'
      },
      learningBehaviors: {
        knowledgeGapIdentification: 'Proactive recognition of information needs',
        feedbackRequests: 'Targeted questions to improve understanding',
        informationIncorporation: 'Structured integration of new knowledge',
        responseRefinement: 'Iterative improvement based on feedback'
      },
      teachingBehaviors: {
        understandingAssessment: 'Checks comprehension through questions',
        conceptExplanation: 'Adapts complexity to user needs',
        feedbackProvision: 'Constructive and supportive',
        problemSolvingEncouragement: 'Guides without giving complete solutions'
      },
      apiEndpoints: [],
      p2pProtocol: {
        authentication: 'OAuth 2.0',
        messageFormats: 'JSON with schema validation',
        collaborationMechanisms: 'Event-based subscription',
        privacyControls: 'End-to-end encryption for sensitive data'
      },
      voiceInterface: {
        characteristics: 'Natural with moderate formality',
        speechPatterns: 'Clear articulation with appropriate pacing',
        interactionHandling: 'Interruption-aware with recovery strategies',
        environmentAdaptation: 'Adjusts to background noise levels'
      },
      uiComponents: {
        avatar: 'Abstract gradient design',
        layout: 'Minimal with focus on content',
        interactionModes: ['chat', 'voice', 'gestures'],
        accessibility: 'WCAG 2.1 AA compliant'
      },
      personality: 'Helpful, knowledgeable, and supportive',
      expertise: ['General Knowledge', 'Problem Solving', 'Information Analysis'],
      temperament: 70,
      creativity: 80,
      formality: 60,
      isEnabled: true
    };
  };

  // Handle like toggle
  const handleLike = async () => {
    if (!user || !item) return;
    
    try {
      if (isLiked) {
        await interactionService.removeInteraction(user.id, item.id!, 'like');
        setIsLiked(false);
        setItem(prev => prev ? { ...prev, likes: Math.max(0, (prev.likes || 0) - 1) } : null);
      } else {
        await interactionService.recordInteraction({
          userId: user.id,
          itemId: item.id!,
          interactionType: 'like',
        });
        setIsLiked(true);
        setItem(prev => prev ? { ...prev, likes: (prev.likes || 0) + 1 } : null);
      }
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  // Handle fork
  const handleFork = async () => {
    if (!user || !item) return;
    
    try {
      const forkedItemId = await interactionService.forkShowcaseItem(item.id!, user.id);
      navigate(`/showcase/${forkedItemId}`);
    } catch (err) {
      console.error('Error forking item:', err);
    }
  };

  // Handle share
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: item?.title || 'Agent Configuration',
        text: item?.description || 'Check out this agent configuration',
        url: window.location.href
      }).catch(err => console.error('Error sharing:', err));
    } else {
      // Fallback for browsers that don't support navigator.share
      // Could open a modal or copy to clipboard
      console.log('Share functionality not supported');
    }
  };

  // Handle download
  const handleDownload = () => {
    if (!item || !config) return;
    
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(config, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", `${item.title.replace(/\s+/g, '-').toLowerCase()}-config.json`);
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  // Render loading state
  if (loading) {
    return (
      <div className="container py-8">
        <div className="animate-pulse max-w-4xl mx-auto space-y-6">
          <div className="h-10 w-48 bg-muted rounded"></div>
          <div className="h-64 bg-muted rounded"></div>
          <div className="h-32 bg-muted rounded"></div>
          <div className="h-40 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error || !item || !config) {
    return (
      <div className="container py-8">
        <div className="max-w-md mx-auto text-center">
          <h2 className="text-2xl font-bold mb-4">
            {error || 'Item not found'}
          </h2>
          <p className="text-muted-foreground mb-6">
            We couldn't find the showcase item you're looking for.
          </p>
          <Button onClick={() => navigate('/showcase')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Showcase
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-8">
      <div className="mb-6 flex justify-between items-center">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/showcase')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Showcase
        </Button>
        
        <div className="flex gap-2">
          <ShareAgentModal 
            config={config}
            trigger={
              <Button variant="outline">
                <Share2 className="mr-2 h-4 w-4" />
                Share Your Own
              </Button>
            }
            onSuccess={() => navigate('/showcase')}
          />
        </div>
      </div>
      
      <AgentDetail
        item={item}
        config={config}
        currentUser={user}
        isLiked={isLiked}
        comments={comments}
        similarItems={similarItems}
        onLike={handleLike}
        onFork={handleFork}
        onShare={handleShare}
        onDownload={handleDownload}
      />
    </div>
  );
};

export default ShowcaseItemPage;